import { useState } from "react";
import {
  Button,
  Field,
  IconButton,
  Text,
  TextInput,
  Tooltip,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtom, useSetAtom } from "jotai";
import { canvasAtom, canvasMetaSetter } from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import getBaseUrl from "../../utils/getBaseUrl";

import styles from "./PublicLink.module.css";

type Props = {
  isViewOnlyMode: boolean;
};

export default function PublicLink({ isViewOnlyMode }: Props) {
  const [canvas, updateCanvas] = useAtom(canvasAtom);
  const saveMeta = useSetAtom(canvasMetaSetter);
  const { emitEventOnPublicLinkSharing } = useEmitUserInteractionEvents();

  function handleChangeCanvasIsPublic(is_public: boolean) {
    updateCanvas((cnvs) => ({
      ...cnvs,
      is_public,
    }));
    void saveMeta({
      is_public,
    });
    emitEventOnPublicLinkSharing(is_public);
  }

  function Description() {
    return (
      <div className={styles.descriptionWrapper}>
        <Text type="Body1">
          A public link allows you to share this canvas as <b>view only</b> with
          those who do not have access to your Zapier account.
        </Text>
      </div>
    );
  }

  function CopyToClipboardField() {
    const [justCopied, setJustCopied] = useState(false);

    const handleCopy = () => {
      copyTextToClipboard(`${getBaseUrl()}/app/canvas/public/${canvas.id}`);
      setJustCopied(true);
      setTimeout(() => {
        setJustCopied(false);
      }, 2000); // Revert after 2 seconds
    };

    return (
      <Field
        label="Public link"
        renderInput={() => (
          <>
            <div
              className={`${styles.copyToClipboardInputRow} ${
                isViewOnlyMode ? styles.copyToClipboardInputRowViewOnly : ""
              }`}
            >
              <TextInput
                isDisabled={true}
                value={`${getBaseUrl()}/app/canvas/public/${canvas.id}`}
                size="medium"
              />
              <div className={styles.copyButtonWrapper}>
                <Tooltip placement="top" content="Copy">
                  <IconButton
                    name="actionCopy"
                    onPress={handleCopy}
                    size="small"
                  />
                </Tooltip>
                {justCopied && (
                  <Text
                    type="SmallPrint2"
                    className={styles.copiedText}
                    color="TextWeaker"
                  >
                    Copied!
                  </Text>
                )}
              </div>
              {!isViewOnlyMode && (
                <Tooltip placement="top" content="Disable public link sharing">
                  <IconButton
                    name="actionTrash"
                    onPress={() => handleChangeCanvasIsPublic(false)}
                    size="small"
                  />
                </Tooltip>
              )}
            </div>
          </>
        )}
      />
    );
  }

  if (isViewOnlyMode) {
    if (!canvas.is_public) {
      return (
        <div className={styles.wrapperViewOnly}>
          <Text type="Body1">
            Request a public link from the canvas owner or an editor.
          </Text>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Description />
        <CopyToClipboardField />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Description />
      {!canvas.is_public ? (
        <div className={styles.buttonWrapper}>
          <Button
            variant="primary"
            size="medium"
            iconPosition="start"
            onClick={() => handleChangeCanvasIsPublic(true)}
          >
            <Icon name="actionLink" />
            Generate public link
          </Button>
        </div>
      ) : (
        <CopyToClipboardField />
      )}
    </div>
  );
}

function copyTextToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}
